import {
  deriveMedicationImpliedConditions,
  deriveSurgicalHistoryImpliedFindings,
} from "./medication-system-categories.js";
import {
  clinicianWorkbenchBasicHistoryKeys,
  clinicianWorkbenchDvtRiskFactorKeys,
  clinicianWorkbenchReviewOfSystemsKeys,
  type ClinicianWorkbenchPatientRecordV1,
  type ClinicianWorkbenchQuestionnaireFieldBinding,
} from "./clinician-workbench-contract.js";
import { createEmptyClinicianWorkbenchPatientRecord } from "./patient-record.js";
import { transformQuestionnaireAnswerV1 } from "./questionnaire-transforms.js";

/**
 * Empty chart record skeleton, shared with the questionnaire projection so a
 * patient who has never been manually charted still gets populated chart
 * forms from their questionnaire answers.
 */
export function createEmptyChartPatientRecord(): ClinicianWorkbenchPatientRecordV1 {
  return createEmptyClinicianWorkbenchPatientRecord();
}

/**
 * Phase O-5 (bug B6): data-driven bindings from the VIP pre-procedure
 * questionnaire onto the nine chart-pack forms' record fields, plus the pure
 * projection that applies them.
 *
 * Rules:
 * - Answers only FILL EMPTY fields — manually charted data always wins.
 * - Every populated field gets fieldMeta { source: "questionnaire" } with the
 *   answer timestamp, so forms can show provenance and review state.
 * - Unknown/unsure answers never populate a field (fail empty, not wrong).
 *
 * fieldPath grammar (interpreted by applyQuestionnaireChartBindings):
 *   clinical.basicHistory.<key>.answer      yes/no -> HistoryItem.answer
 *   clinical.basicHistory.<key>.detail      text  -> HistoryItem.detail
 *   clinical.dvtRiskFactors.<key>           yes/no -> boolean map
 *   clinical.reviewOfSystems.<key>.finding  yes -> append {code,label} finding
 *   clinical.reviewOfSystems.<key>.detail   text -> append to detail
 *   clinical.noKnownMedications             "no" -> true (noneFlag)
 *   clinical.noKnownAllergies               "no" -> true (noneFlag)
 *   clinical.medications                    medication entries array
 *   clinical.allergies.<category>           yes/text -> allergy entry
 *   clinical.surgicalHistory                text -> past-surgery entry
 *   clinical.anesthesiaHistory.<field>      yes/no or text
 */

type Binding = ClinicianWorkbenchQuestionnaireFieldBinding & {
  findingLabel?: string;
};

const basicHistory = (
  questionKey: string,
  key: string,
  part: "answer" | "detail" = "answer",
): Binding => ({
  questionKey,
  fieldPath: `clinical.basicHistory.${key}.${part}`,
  transform: part === "answer" ? "yesNo" : "text",
});

const ros = (
  questionKey: string,
  system: string,
  label: string,
): Binding => ({
  questionKey,
  fieldPath: `clinical.reviewOfSystems.${system}.finding`,
  transform: "yesNo",
  findingLabel: label,
});

const rosDetail = (questionKey: string, system: string): Binding => ({
  questionKey,
  fieldPath: `clinical.reviewOfSystems.${system}.detail`,
  transform: "text",
});

const dvt = (questionKey: string, key: string): Binding => ({
  questionKey,
  fieldPath: `clinical.dvtRiskFactors.${key}`,
  transform: "yesNo",
});

export const vipQuestionnaireChartBindings: Binding[] = [
  // Medications (Pre-Op Record, H&P, History Form)
  { questionKey: "takes_current_medications", fieldPath: "clinical.noKnownMedications", transform: "yesNo" },
  { questionKey: "current_medications_detail", fieldPath: "clinical.medications", transform: "list" },
  // Allergies (Anesthesia forms, Pre-Op Record)
  { questionKey: "medication_allergies", fieldPath: "clinical.noKnownAllergies", transform: "yesNo" },
  { questionKey: "medication_allergy_reactions", fieldPath: "clinical.allergies.medication", transform: "text" },
  { questionKey: "other_allergy_reactions", fieldPath: "clinical.allergies.other", transform: "text" },
  { questionKey: "latex_products_allergy", fieldPath: "clinical.allergies.latex", transform: "yesNo" },
  { questionKey: "adhesive_tape_allergy", fieldPath: "clinical.allergies.adhesive", transform: "yesNo" },
  { questionKey: "iv_contrast_xray_dye_allergy", fieldPath: "clinical.allergies.contrast", transform: "yesNo" },
  { questionKey: "environmental_allergy_detail", fieldPath: "clinical.allergies.environmental", transform: "text" },
  { questionKey: "food_allergy_detail", fieldPath: "clinical.allergies.food", transform: "text" },
  // Surgery & anesthesia history (H&P, Anesthesia Physical Exam, Consent)
  { questionKey: "prior_surgeries_detail", fieldPath: "clinical.surgicalHistory", transform: "text" },
  { questionKey: "malignant_hyperthermia_history", fieldPath: "clinical.anesthesiaHistory.malignantHyperthermia", transform: "yesNo" },
  { questionKey: "malignant_hyperthermia_who", fieldPath: "clinical.anesthesiaHistory.malignantHyperthermiaWho", transform: "text" },
  { questionKey: "pseudocholinesterase_deficiency", fieldPath: "clinical.anesthesiaHistory.pseudocholinesteraseDeficiency", transform: "yesNo" },
  { questionKey: "ponv_history", fieldPath: "clinical.anesthesiaHistory.postoperativeNauseaVomiting", transform: "yesNo" },
  { questionKey: "motion_sickness", fieldPath: "clinical.anesthesiaHistory.motionSickness", transform: "yesNo" },
  // v11 umbrella: a "no" here means every anesthesia screen is negative
  // (derived post-loop); a "yes" flags the section for review.
  { questionKey: "anesthesia_problems_umbrella", fieldPath: "clinical.anesthesiaHistory.priorComplications", transform: "yesNo" },
  { questionKey: "other_anesthesia_problem", fieldPath: "clinical.anesthesiaHistory.priorComplications", transform: "yesNo" },
  { questionKey: "other_anesthesia_problem_detail", fieldPath: "clinical.anesthesiaHistory.otherDescription", transform: "text" },
  // Social & basic history (Fall Risk, Pre-Op Record). v11 (Shannon
  // 2026-09-01) retired glasses/contacts/blind/hearing aids/dental/
  // artificial joints/other tobacco/COVID — those chart slots are manual-only.
  basicHistory("ever_smoked", "smoking"),
  basicHistory("smoking_start_quit_year", "smoking", "detail"),
  basicHistory("drinks_alcohol", "alcohol"),
  basicHistory("alcohol_drinks_per_week", "alcohol", "detail"),
  basicHistory("recreational_or_street_drugs", "streetDrugs"),
  basicHistory("recreational_or_street_drugs_detail", "streetDrugs", "detail"),
  basicHistory("mobility_problems", "mobilityProblems"),
  basicHistory("mobility_problem_detail", "mobilityProblems", "detail"),
  basicHistory("body_piercing_other_than_earrings", "bodyPiercings"),
  basicHistory("metal_implants", "metalImplants"),
  basicHistory("metal_implants_detail", "metalImplants", "detail"),
  basicHistory("pacemaker", "pacemaker"),
  basicHistory("aicd", "aicd"),
  basicHistory("cardiac_stent", "cardiacStent"),
  basicHistory("cardiac_stent_date", "cardiacStent", "detail"),
  basicHistory("internal_nerve_stimulator", "nerveStimulator"),
  // v11 (Shannon 2026-09-01): devices print under cardiovascular; implants,
  // stimulators, mobility and chronic pain under musculoskeletal.
  ros("pacemaker", "cardiovascular", "Pacemaker"),
  rosDetail("pacemaker_manufacturer", "cardiovascular"),
  ros("aicd", "cardiovascular", "AICD"),
  ros("cardiac_stent", "cardiovascular", "Cardiac stent"),
  rosDetail("cardiac_stent_date", "cardiovascular"),
  ros("metal_implants", "musculoskeletal", "Metal implants"),
  rosDetail("metal_implants_detail", "musculoskeletal"),
  ros("internal_nerve_stimulator", "musculoskeletal", "Internal nerve stimulator"),
  ros("mobility_problems", "musculoskeletal", "Mobility problems"),
  rosDetail("mobility_problem_detail", "musculoskeletal"),
  ros("chronic_pain", "musculoskeletal", "Chronic pain"),
  rosDetail("chronic_pain_score", "musculoskeletal"),
  // NOTE: the chart's fall-risk keys (dizzinessVertigo, historyOfFalls,
  // confusion, medsCausingDizziness) have NO corresponding questions in the
  // current VIP template — the Fall Risk form cannot score from this
  // questionnaire until those questions are added to the template.
  // Blood thinners — red-flag preop data (Pre-Op Record, H&P)
  ros("recent_blood_thinners", "hematological", "Recent blood thinners"),
  rosDetail("recent_blood_thinners_detail", "hematological"),
  ros("cardiac_stent_drug_eluting", "cardiovascular", "Drug-eluting stent"),
  ros("paralysis_musculoskeletal", "musculoskeletal", "Paralysis"),
  // Surgery-history gate: an explicit "no" charts No Surgical History.
  { questionKey: "prior_surgeries", fieldPath: "clinical.noSurgicalHistory", transform: "yesNo" },
  // Allergy gates without detail still chart an entry on "yes".
  { questionKey: "other_allergies", fieldPath: "clinical.allergies.other", transform: "yesNo" },
  { questionKey: "food_allergies", fieldPath: "clinical.allergies.food", transform: "yesNo" },
  { questionKey: "environmental_allergies", fieldPath: "clinical.allergies.environmental", transform: "yesNo" },
  // Ride-home / caregiver contacts — required chart-pack fields.
  { questionKey: "ride_home_name_relationship_phone", fieldPath: "contacts.rideHome", transform: "text" },
  { questionKey: "after_surgery_caregiver_details", fieldPath: "contacts.caregiver", transform: "text" },
  // Cardiovascular ROS (H&P, Anesthesia Physical Exam)
  ros("angina", "cardiovascular", "Angina"),
  rosDetail("angina_detail", "cardiovascular"),
  ros("myocardial_infarction", "cardiovascular", "Myocardial infarction"),
  rosDetail("myocardial_infarction_date", "cardiovascular"),
  ros("congestive_heart_failure", "cardiovascular", "Congestive heart failure"),
  ros("abnormal_heart_rhythm", "cardiovascular", "Abnormal heart rhythm"),
  rosDetail("abnormal_heart_rhythm_type", "cardiovascular"),
  ros("high_blood_pressure", "cardiovascular", "High blood pressure"),
  ros("heart_valve_problems", "cardiovascular", "Heart valve problems"),
  ros("vascular_disease", "cardiovascular", "Vascular disease"),
  ros("high_cholesterol_or_lipids", "cardiovascular", "High cholesterol"),
  ros("bypass_surgery", "cardiovascular", "Bypass surgery"),
  rosDetail("bypass_surgery_date", "cardiovascular"),
  ros("cardiovascular_other", "cardiovascular", "Other cardiovascular"),
  rosDetail("cardiovascular_other_detail", "cardiovascular"),
  // Pulmonary ROS
  ros("emphysema_copd", "pulmonary", "Emphysema / COPD"),
  ros("asthma", "pulmonary", "Asthma"),
  rosDetail("asthma_attack_last_date", "pulmonary"),
  ros("pneumonia_last_six_weeks", "pulmonary", "Recent pneumonia"),
  ros("home_oxygen_use", "pulmonary", "Home oxygen"),
  ros("sleep_apnea", "pulmonary", "Sleep apnea"),
  ros("cpap_bipap_use", "pulmonary", "CPAP / BiPAP"),
  ros("tuberculosis", "pulmonary", "Tuberculosis"),
  ros("bronchitis", "pulmonary", "Bronchitis"),
  ros("shortness_of_breath", "pulmonary", "Shortness of breath"),
  ros("recent_cough_or_cold", "pulmonary", "Recent cough or cold"),
  ros("pulmonary_other", "pulmonary", "Other pulmonary"),
  rosDetail("pulmonary_other_detail", "pulmonary"),
  // Renal / hepatic / neuro / GI / endocrine ROS
  ros("kidney_stones", "renal", "Kidney stones"),
  ros("prostate_trouble", "renal", "Prostate trouble"),
  ros("urinary_incontinence", "renal", "Urinary incontinence"),
  ros("kidney_failure", "renal", "Kidney failure"),
  ros("dialysis", "renal", "Dialysis"),
  rosDetail("dialysis_detail", "renal"),
  rosDetail("renal_other_detail", "renal"),
  ros("liver_failure_or_jaundice", "hepatic", "Liver failure / jaundice"),
  ros("hepatitis", "hepatic", "Hepatitis"),
  rosDetail("hepatitis_type", "hepatic"),
  ros("cirrhosis", "hepatic", "Cirrhosis"),
  rosDetail("hepatic_other_detail", "hepatic"),
  ros("seizures", "neurological", "Seizures"),
  rosDetail("seizures_detail", "neurological"),
  ros("stroke_tia", "neurological", "Stroke / TIA"),
  rosDetail("stroke_tia_date", "neurological"),
  ros("neuropathy", "neurological", "Neuropathy"),
  ros("frequent_migraines", "neurological", "Frequent migraines"),
  ros("paralysis_neuro", "neurological", "Paralysis"),
  ros("alzheimers", "neurological", "Alzheimer's"),
  ros("parkinsons", "neurological", "Parkinson's"),
  rosDetail("neurologic_other_detail", "neurological"),
  ros("stomach_ulcers", "gastrointestinal", "Stomach ulcers"),
  ros("acid_reflux_heartburn", "gastrointestinal", "Acid reflux / heartburn"),
  ros("hiatal_hernia", "gastrointestinal", "Hiatal hernia"),
  rosDetail("gastrointestinal_other_detail", "gastrointestinal"),
  ros("thyroid_disease", "endocrine", "Thyroid disease"),
  rosDetail("thyroid_type", "endocrine"),
  ros("diabetes", "endocrine", "Diabetes"),
  rosDetail("diabetes_type", "endocrine"),
  rosDetail("endocrine_other_detail", "endocrine"),
  // Oncologic / infectious / hematologic ROS + DVT feeders
  ros("cancer_history", "oncologic", "Cancer history"),
  rosDetail("cancer_detail", "oncologic"),
  ros("mrsa_history", "infectiousDiseases", "MRSA history"),
  rosDetail("mrsa_detail", "infectiousDiseases"),
  ros("recent_infectious_symptoms_7_days", "infectiousDiseases", "Recent infectious symptoms"),
  rosDetail("infectious_other_detail", "infectiousDiseases"),
  ros("anemia", "hematological", "Anemia"),
  ros("blood_clots_history", "hematological", "Blood clots history"),
  rosDetail("blood_clots_who", "hematological"),
  ros("bleeding_or_clotting_disorder", "hematological", "Bleeding / clotting disorder"),
  rosDetail("bleeding_or_clotting_disorder_name", "hematological"),
  ros("blood_transfusion", "hematological", "Prior blood transfusion"),
  ros("hiv_or_aids", "infectiousDiseases", "HIV / AIDS"),
  rosDetail("hematologic_other_detail", "hematological"),
  dvt("bleeding_or_clotting_disorder", "bloodClottingDisorders"),
  dvt("cancer_history", "cancer"),
  dvt("currently_pregnant", "currentlyPregnant"),
  // Musculoskeletal / psychiatric ROS
  ros("musculoskeletal_problems", "musculoskeletal", "Musculoskeletal problems"),
  ros("arthritis", "musculoskeletal", "Arthritis"),
  rosDetail("arthritis_type", "musculoskeletal"),
  ros("chronic_low_back_pain", "musculoskeletal", "Chronic low back pain"),
  ros("neck_or_jaw_problems", "musculoskeletal", "Neck or jaw problems"),
  ros("muscular_dystrophy", "musculoskeletal", "Muscular dystrophy"),
  rosDetail("paralysis_musculoskeletal_location", "musculoskeletal"),
  rosDetail("musculoskeletal_other_detail", "musculoskeletal"),
  ros("bipolar_disorder", "psychiatric", "Bipolar disorder"),
  ros("schizophrenia", "psychiatric", "Schizophrenia"),
  ros("depression", "psychiatric", "Depression"),
  ros("panic_anxiety_attacks", "psychiatric", "Panic / anxiety attacks"),
  ros("autism", "psychiatric", "Autism"),
  ros("ptsd", "psychiatric", "PTSD"),
  rosDetail("psychiatric_other_detail", "psychiatric"),
  // OB/GYN + final
  ros("currently_pregnant", "obgyn", "Currently pregnant"),
  rosDetail("last_menstrual_period_date", "obgyn"),
  ros("hysterectomy", "obgyn", "Hysterectomy"),
  ros("bilateral_tubal_ligation", "obgyn", "Bilateral tubal ligation"),
  ros("menopause", "obgyn", "Menopause"),
  rosDetail("additional_comments_or_history", "other"),
];

export interface QuestionnaireChartAnswer {
  value: unknown;
  /** Who produced this answer. "clinician" marks a manual entry: on a
   *  pre-operative chart "the patient told us" and "a nurse recorded" are
   *  different clinical claims, so the distinction has to survive into
   *  fieldMeta rather than flattening to "questionnaire" for everything. */
  source?: string | null;
  medications?: Array<{
    patientReportedText?: string | null;
    drugName?: string | null;
    strength?: string | null;
    matchConfidence?: string | null;
  }>;
  answeredAt?: string | null;
}

function yesNoOf(value: unknown): boolean | null {
  if (value === true || value === false) {
    return value;
  }

  if (typeof value === "string") {
    const v = value.trim().toLowerCase();

    if (["yes", "true", "y"].includes(v)) {
      return true;
    }

    if (["no", "false", "n"].includes(v)) {
      return false;
    }
  }

  return null;
}

function textOf(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function isEmpty(value: unknown): boolean {
  return (
    value == null ||
    (typeof value === "string" && value.trim().length === 0) ||
    (Array.isArray(value) && value.length === 0)
  );
}

/** "Ancef — anaphylaxis; sulfa, hives; latex: swelling" -> one entry per
 *  allergen. An item without a separator is the substance alone. */
function parseAllergyItems(
  text: string,
  fallbackSubstance: string,
): Array<{ substance: string; reaction: string | null }> {
  const items = text
    .split(/\s*;\s*|\s+and\s+(?=[A-Za-z])/u)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  if (items.length === 0) {
    return [{ substance: fallbackSubstance, reaction: text }];
  }

  return items.map((item) => {
    const match = item.match(/^(.+?)\s*(?:[—–-]|:|,)\s*(.+)$/u);

    if (match && match[1].trim().length > 0) {
      return { substance: match[1].trim(), reaction: match[2].trim() || null };
    }

    return { substance: item, reaction: null };
  });
}

const ALLERGY_SUBSTANCES: Record<string, { category: string; substance: string }> = {
  medication: { category: "medication", substance: "Medication allergy (see reactions)" },
  other: { category: "other", substance: "Other allergy (see reactions)" },
  latex: { category: "latex", substance: "Latex products" },
  adhesive: { category: "other", substance: "Adhesive tape" },
  contrast: { category: "other", substance: "IV contrast / x-ray dye" },
  environmental: { category: "environmental", substance: "Environmental allergy" },
  food: { category: "food", substance: "Food allergy" },
};

/**
 * Apply the bindings onto a record, filling only empty fields, and stamp
 * fieldMeta provenance. Returns the count of populated field paths.
 */
export function applyQuestionnaireChartBindings(input: {
  record: ClinicianWorkbenchPatientRecordV1;
  answers: Record<string, QuestionnaireChartAnswer>;
  bindings?: Binding[];
  appliedAt?: string;
}): { populatedFieldPaths: string[] } {
  const record = input.record as unknown as {
    clinical: Record<string, unknown>;
    fieldMeta: Record<string, { source: string; updatedAt: string; updatedBy: string }>;
  };
  const bindings = input.bindings ?? vipQuestionnaireChartBindings;
  const populated: string[] = [];
  const clinical = record.clinical;

  const stamp = (
    fieldPath: string,
    answeredAt: string | null | undefined,
    answerSource?: string | null,
  ): void => {
    populated.push(fieldPath);
    record.fieldMeta[fieldPath] = {
      source: answerSource === "clinician" ? "clinician" : "questionnaire",
      updatedAt: answeredAt ?? input.appliedAt ?? "",
      updatedBy: "questionnaire-projection",
    };
  };

  for (const binding of bindings) {
    const answer = input.answers[binding.questionKey];

    if (!answer) {
      continue;
    }

    const parts = binding.fieldPath.split(".");

    // clinical.basicHistory.<key>.<answer|detail>
    if (parts[1] === "basicHistory" && parts.length === 4) {
      const items = (clinical.basicHistory ??= {}) as Record<
        string,
        { answer: boolean | null; detail: string | null }
      >;
      const item = (items[parts[2]] ??= { answer: null, detail: null });

      if (parts[3] === "answer") {
        const yn = yesNoOf(answer.value);

        if (yn !== null && item.answer == null) {
          item.answer = yn;
          stamp(`clinical.basicHistory.${parts[2]}`, answer.answeredAt, answer.source);
        }
      } else {
        const text = textOf(answer.value);

        if (text && isEmpty(item.detail)) {
          item.detail = text;
          stamp(`clinical.basicHistory.${parts[2]}`, answer.answeredAt, answer.source);
        }
      }

      continue;
    }

    // clinical.dvtRiskFactors.<key>
    if (parts[1] === "dvtRiskFactors" && parts.length === 3) {
      const factors = (clinical.dvtRiskFactors ??= {}) as Record<string, boolean | null>;
      const yn = yesNoOf(answer.value);

      if (yn !== null && factors[parts[2]] == null) {
        factors[parts[2]] = yn;
        stamp(binding.fieldPath, answer.answeredAt, answer.source);
      }

      continue;
    }

    // clinical.reviewOfSystems.<key>.<finding|detail>
    if (parts[1] === "reviewOfSystems" && parts.length === 4) {
      const systems = (clinical.reviewOfSystems ??= {}) as Record<
        string,
        { findings: Array<{ code: string; label: string }>; detail: string | null }
      >;
      const system = (systems[parts[2]] ??= { findings: [], detail: null });

      // Manual wins for the whole system: once a clinician has charted this
      // system (meta absent = untouched; meta from us = ours to extend),
      // the questionnaire never appends findings or detail to it.
      const systemPath = `clinical.reviewOfSystems.${parts[2]}`;
      const systemMeta = record.fieldMeta[systemPath];

      if (systemMeta && systemMeta.updatedBy !== "questionnaire-projection") {
        continue;
      }

      if (parts[3] === "finding") {
        if (
          yesNoOf(answer.value) === true &&
          !system.findings.some((finding) => finding.code === binding.questionKey)
        ) {
          system.findings.push({
            code: binding.questionKey,
            label: binding.findingLabel ?? binding.questionKey,
            origin: "questionnaire",
          } as { code: string; label: string });
          stamp(systemPath, answer.answeredAt, answer.source);
        }
      } else {
        const text = textOf(answer.value);

        if (text) {
          const label = binding.findingLabel ?? binding.questionKey.replace(/_/gu, " ");
          const line = `${label}: ${text}`;

          if (!(system.detail ?? "").split("; ").includes(line)) {
            system.detail = system.detail ? `${system.detail}; ${line}` : line;
            stamp(systemPath, answer.answeredAt, answer.source);
          }
        }
      }

      continue;
    }

    // clinical.noKnownMedications: an explicit "no" to the single gate.
    // (noKnownAllergies is resolved AFTER the loop from all four allergy
    // gates together — a lone medication-allergy "no" must not print
    // "No known allergies" while a food/environmental gate said yes.)
    if (binding.fieldPath === "clinical.noKnownMedications") {
      if (yesNoOf(answer.value) === false && clinical.noKnownMedications == null) {
        clinical.noKnownMedications = true;
        stamp(binding.fieldPath, answer.answeredAt, answer.source);
      }

      continue;
    }

    if (binding.fieldPath === "clinical.noKnownAllergies") {
      continue; // handled post-loop
    }

    // clinical.noSurgicalHistory: prior_surgeries gate "no".
    if (binding.fieldPath === "clinical.noSurgicalHistory") {
      if (yesNoOf(answer.value) === false && clinical.noSurgicalHistory == null) {
        clinical.noSurgicalHistory = true;
        stamp(binding.fieldPath, answer.answeredAt, answer.source);
      }

      continue;
    }

    // clinical.medications: voice-captured medication entries. A manually
    // charted "no known medications" wins over questionnaire entries.
    if (binding.fieldPath === "clinical.medications") {
      const noneFlagMeta = record.fieldMeta["clinical.noKnownMedications"];
      const manualNone =
        clinical.noKnownMedications === true &&
        (!noneFlagMeta || noneFlagMeta.updatedBy !== "questionnaire-projection");

      if (manualNone) {
        continue;
      }

      const medications = (clinical.medications ??= []) as Array<{
        name: string;
        dose: string | null;
        frequency: string | null;
        reason: string | null;
      }>;

      if (medications.length === 0 && (answer.medications?.length ?? 0) > 0) {
        for (const entry of answer.medications ?? []) {
          const name = textOf(entry.drugName) ?? textOf(entry.patientReportedText);

          if (name) {
            // The caveat lives in the reason column, never inside the drug
            // name — the name cell of a signed chart must stay clean.
            medications.push({
              name,
              dose: textOf(entry.strength),
              frequency: null,
              reason:
                entry.matchConfidence === "high"
                  ? null
                  : "Unconfirmed — patient reported; pending nurse review",
              origin: "questionnaire",
            } as never);
          }
        }

        if (medications.length > 0) {
          clinical.noKnownMedications = false;
          stamp(binding.fieldPath, answer.answeredAt, answer.source);
        }
      }

      continue;
    }

    // clinical.allergies.<kind> — a manually charted "no known allergies"
    // wins; questionnaire entries never flip a manual none-flag.
    if (parts[1] === "allergies" && parts.length === 3) {
      const spec = ALLERGY_SUBSTANCES[parts[2]];

      if (!spec) {
        continue;
      }

      const allergyFlagMeta = record.fieldMeta["clinical.noKnownAllergies"];
      const manualNoAllergies =
        clinical.noKnownAllergies === true &&
        (!allergyFlagMeta || allergyFlagMeta.updatedBy !== "questionnaire-projection");

      if (manualNoAllergies) {
        continue;
      }

      const allergies = (clinical.allergies ??= []) as Array<{
        category: string;
        substance: string;
        reaction: string | null;
      }>;
      const yn = yesNoOf(answer.value);
      const text = textOf(answer.value);
      const shouldAdd = binding.transform === "yesNo" ? yn === true : text != null;

      if (!shouldAdd) {
        continue;
      }

      // v12: allergy details arrive as free text, one "substance — reaction"
      // per item ("Ancef — anaphylaxis; sulfa — hives"). Each becomes its own
      // chart entry with the allergen NAMED — the reaction-only shape left a
      // live caller's Ancef, sulfa and latex-anaphylaxis out of the chart.
      const entries: Array<{ substance: string; reaction: string | null }> =
        binding.transform === "yesNo" || text == null
          ? [{ substance: spec.substance, reaction: null }]
          : parseAllergyItems(text, spec.substance);
      const known = (value: string): string => value.trim().toLowerCase();

      for (const entry of entries) {
        if (allergies.some((a) => known(a.substance) === known(entry.substance))) {
          continue;
        }

        allergies.push({
          category: spec.category,
          substance: entry.substance,
          reaction: entry.reaction,
          origin: "questionnaire",
        } as never);
        clinical.noKnownAllergies = false;
        stamp("clinical.allergies", answer.answeredAt, answer.source);
      }

      continue;
    }

    // clinical.surgicalHistory: free-text prior surgeries. Manual "no
    // surgical history" wins.
    if (binding.fieldPath === "clinical.surgicalHistory") {
      const surgFlagMeta = record.fieldMeta["clinical.noSurgicalHistory"];
      const manualNoSurgery =
        clinical.noSurgicalHistory === true &&
        (!surgFlagMeta || surgFlagMeta.updatedBy !== "questionnaire-projection");

      if (manualNoSurgery) {
        continue;
      }

      const history = (clinical.surgicalHistory ??= []) as Array<{
        surgery: string;
        approximateDate: string | null;
        problems: string | null;
      }>;
      const text = textOf(answer.value);

      if (text && history.length === 0) {
        history.push({
          surgery: text,
          approximateDate: null,
          problems: null,
          origin: "questionnaire",
        } as never);
        clinical.noSurgicalHistory = false;
        stamp(binding.fieldPath, answer.answeredAt, answer.source);
      }

      continue;
    }

    // clinical.anesthesiaHistory.<field>
    if (parts[1] === "anesthesiaHistory" && parts.length === 3) {
      const anesthesia = (clinical.anesthesiaHistory ??= {}) as Record<string, unknown>;
      const field = parts[2];
      const next =
        binding.transform === "yesNo" ? yesNoOf(answer.value) : textOf(answer.value);

      if (next != null && anesthesia[field] == null) {
        anesthesia[field] = next;
        stamp(binding.fieldPath, answer.answeredAt, answer.source);
      }

      continue;
    }

    // contacts.rideHome / contacts.caregiver — required chart-pack contacts.
    if (parts[0] === "contacts" && parts.length === 2) {
      const contacts = (input.record as unknown as {
        contacts: Record<string, { name: string | null }>;
      }).contacts;
      const contact = contacts?.[parts[1]];
      const text = textOf(answer.value);

      if (contact && text && isEmpty(contact.name)) {
        contact.name = text;
        stamp(binding.fieldPath, answer.answeredAt, answer.source);
      }

      continue;
    }
  }

  // noKnownAllergies: only when EVERY allergy gate answered an explicit "no"
  // (a dropped call after a "yes" gate must fail empty, never fail wrong).
  const allergyGates = [
    "medication_allergies",
    "other_allergies",
    "food_allergies",
    "environmental_allergies",
  ];
  const allGatesNo = allergyGates.every(
    (gate) => yesNoOf(input.answers[gate]?.value) === false,
  );

  if (allGatesNo && clinical.noKnownAllergies == null) {
    clinical.noKnownAllergies = true;
    stamp(
      "clinical.noKnownAllergies",
      input.answers.medication_allergies?.answeredAt,
    );
  }

  // v11 anesthesia umbrella "no": every screen it gates is negative — the
  // chart prints "no history of problems with anesthesia; denies malignant
  // hyperthermia" from these.
  const anesthesiaUmbrella = input.answers.anesthesia_problems_umbrella;

  if (anesthesiaUmbrella && yesNoOf(anesthesiaUmbrella.value) === false) {
    const anesthesia = (clinical.anesthesiaHistory ??= {}) as Record<string, unknown>;

    for (const field of [
      "malignantHyperthermia",
      "pseudocholinesteraseDeficiency",
      "postoperativeNauseaVomiting",
      "motionSickness",
      "priorComplications",
    ]) {
      if (anesthesia[field] == null) {
        anesthesia[field] = false;
        stamp(`clinical.anesthesiaHistory.${field}`, anesthesiaUmbrella.answeredAt, anesthesiaUmbrella.source);
      }
    }
  }

  applyImpliedFindings(clinical, record.fieldMeta, stamp);

  return { populatedFieldPaths: populated };
}

/**
 * Review-of-systems findings the chart can state from what it already holds:
 * medication-implied conditions ("Hypertension (on lisinopril)") and
 * surgical-history-implied devices and implants ("Pacemaker (per surgical
 * history)"). Shannon (2026-09-01): "from medications alone... it should say
 * hypertension... if I've had a pacemaker it should say pacemaker under
 * cardiovascular". Never touches a system a clinician charted by hand, never
 * duplicates a finding, and carries the questionnaire origin so the
 * persistence strip removes it with the rest of the overlay.
 */
function applyImpliedFindings(
  clinical: Record<string, unknown>,
  fieldMeta: Record<string, { source: string; updatedAt: string; updatedBy: string }>,
  stamp: (fieldPath: string, answeredAt?: string | null, source?: string | null) => void,
): void {
  const systems = (clinical.reviewOfSystems ??= {}) as Record<
    string,
    { findings: Array<{ code: string; label: string; origin?: string }>; detail: string | null }
  >;
  const addFinding = (systemKey: string, code: string, label: string): void => {
    const systemPath = `clinical.reviewOfSystems.${systemKey}`;
    const systemMeta = fieldMeta[systemPath];

    if (systemMeta && systemMeta.updatedBy !== "questionnaire-projection") {
      return;
    }

    const system = (systems[systemKey] ??= { findings: [], detail: null });
    const conditionOf = (text: string): string => text.split(" (")[0].toLowerCase();

    if (
      system.findings.some(
        (finding) => finding.code === code || conditionOf(finding.label) === conditionOf(label),
      )
    ) {
      return;
    }

    system.findings.push({ code, label, origin: "questionnaire" });
    stamp(systemPath, null, null);
  };

  const medications = (clinical.medications ?? []) as Array<{ name?: string | null }>;
  const medicationTexts = medications
    .map((medication) => medication.name ?? "")
    .filter((name) => name.length > 0);

  for (const implied of deriveMedicationImpliedConditions(medicationTexts)) {
    addFinding(implied.system, `implied:${implied.generic}`, `${implied.condition} (on ${implied.generic})`);
  }

  const surgeries = (clinical.surgicalHistory ?? []) as Array<{ surgery?: string | null }>;
  const surgeryTexts = surgeries
    .map((surgery) => surgery.surgery ?? "")
    .filter((text) => text.length > 0);

  for (const implied of deriveSurgicalHistoryImpliedFindings(surgeryTexts)) {
    addFinding(implied.system, implied.code, implied.label);
  }
}

// ---------------------------------------------------------------------------
// Generic overlay — template-version chart bindings from the tenant database
// ---------------------------------------------------------------------------

/** Prefixes a DB-supplied binding may write. Bindings are tenant-admin data,
 *  so the overlay refuses paths outside the questionnaire-bindable record
 *  areas (fieldMeta especially must never be binding-addressable). */
const genericOverlayAllowedPrefixes = [
  "patient.",
  "contacts.",
  "clinical.questionnaireAnswers.",
  "clinical.structuredDetails.",
  "clinical.basicHistory.",
] as const;

const unsafePathSegments = new Set(["__proto__", "constructor", "prototype"]);

function genericOverlayPathAllowed(fieldPath: string): boolean {
  return (
    genericOverlayAllowedPrefixes.some((prefix) =>
      fieldPath.startsWith(prefix),
    ) && fieldPath.split(".").every((part) => !unsafePathSegments.has(part))
  );
}

function readGenericPath(root: Record<string, unknown>, fieldPath: string): unknown {
  return fieldPath.split(".").reduce<unknown>((cursor, segment) => {
    if (cursor == null || typeof cursor !== "object") return undefined;
    return (cursor as Record<string, unknown>)[segment];
  }, root);
}

function writeGenericPath(
  root: Record<string, unknown>,
  fieldPath: string,
  value: unknown,
): void {
  const segments = fieldPath.split(".");
  let cursor: Record<string, unknown> = root;

  for (const segment of segments.slice(0, -1)) {
    if (cursor[segment] == null || typeof cursor[segment] !== "object") {
      cursor[segment] = {};
    }

    cursor = cursor[segment] as Record<string, unknown>;
  }

  cursor[segments[segments.length - 1]] = value;
}

/**
 * Overlay a template version's generic chart bindings (the DB-served
 * questionKey → fieldPath → transform table) onto a record. Same contract as
 * applyQuestionnaireChartBindings: fill-empty-only, manual charting wins,
 * every write stamped with questionnaire-projection provenance so
 * stripQuestionnaireChartProjection removes it before any persist.
 * Untransformable answers are skipped, never written as nulls.
 */
export function applyGenericQuestionnaireChartOverlay(input: {
  record: ClinicianWorkbenchPatientRecordV1;
  answers: Record<string, QuestionnaireChartAnswer>;
  bindings: ClinicianWorkbenchQuestionnaireFieldBinding[];
  appliedAt?: string;
}): { populatedFieldPaths: string[] } {
  const record = input.record as unknown as Record<string, unknown> & {
    fieldMeta: Record<string, { source: string; updatedAt: string; updatedBy: string }>;
  };
  const populated: string[] = [];
  record.fieldMeta ??= {};

  const stamp = (
    fieldPath: string,
    answeredAt: string | null | undefined,
    answerSource?: string | null,
  ): void => {
    populated.push(fieldPath);
    record.fieldMeta[fieldPath] = {
      source: answerSource === "clinician" ? "clinician" : "questionnaire",
      updatedAt: answeredAt ?? input.appliedAt ?? "",
      updatedBy: "questionnaire-projection",
    };
  };

  for (const binding of input.bindings) {
    const answer = input.answers[binding.questionKey];

    if (!answer || !genericOverlayPathAllowed(binding.fieldPath)) {
      continue;
    }

    const value = transformQuestionnaireAnswerV1(
      answer.value,
      binding.transform ?? "verbatim",
    );

    if (value === null || value === undefined || value === "") {
      continue;
    }

    // basicHistory pairs stamp the item path (matching the strip contract and
    // the hand-authored binding table) rather than the .answer/.detail leaf.
    const parts = binding.fieldPath.split(".");
    const stampPath =
      parts[1] === "basicHistory" && parts.length === 4
        ? parts.slice(0, 3).join(".")
        : binding.fieldPath;
    const existingMeta = record.fieldMeta[stampPath];

    if (existingMeta && existingMeta.updatedBy !== "questionnaire-projection") {
      // Manually charted (or imported) — the overlay never overrides it.
      continue;
    }

    const current = readGenericPath(record, binding.fieldPath);

    if (!isEmpty(current) && typeof current !== "object") {
      // Shared text slots (several detail questions feeding one field): append
      // when the existing text came from this projection, otherwise leave the
      // manually charted value untouched.
      if (
        typeof current === "string" &&
        typeof value === "string" &&
        existingMeta?.updatedBy === "questionnaire-projection" &&
        !current.split("; ").includes(value)
      ) {
        writeGenericPath(record, binding.fieldPath, `${current}; ${value}`);
        stamp(stampPath, answer.answeredAt, answer.source);
      }

      continue;
    }

    if (Array.isArray(current) && current.length > 0) {
      continue;
    }

    if (
      current !== null &&
      current !== undefined &&
      typeof current === "object" &&
      !Array.isArray(current)
    ) {
      // The binding must address a leaf, not a container.
      continue;
    }

    writeGenericPath(record, binding.fieldPath, value);
    stamp(stampPath, answer.answeredAt, answer.source);
  }

  return { populatedFieldPaths: populated };
}

/**
 * Remove everything the questionnaire projection added, so a chart record
 * can be PERSISTED without freezing overlay data into the chart of record.
 * The overlay is recomputed on every read; persisting it would block later
 * medication resolution and answer redaction from ever reaching the chart.
 */
export function stripQuestionnaireChartProjection(
  record: ClinicianWorkbenchPatientRecordV1,
): void {
  const mutable = record as unknown as {
    clinical: Record<string, unknown>;
    contacts?: Record<string, { name: string | null }>;
    fieldMeta: Record<string, { source?: string; updatedBy?: string }>;
  };
  const clinical = mutable.clinical ?? {};
  const stripOrigin = <T extends { origin?: string }>(items: unknown): T[] =>
    (Array.isArray(items) ? items : []).filter(
      (item) => (item as T)?.origin !== "questionnaire",
    ) as T[];

  clinical.medications = stripOrigin(clinical.medications);
  clinical.allergies = stripOrigin(clinical.allergies);
  clinical.surgicalHistory = stripOrigin(clinical.surgicalHistory);

  const questionnairePaths = Object.entries(mutable.fieldMeta ?? {})
    .filter(([, meta]) => meta?.updatedBy === "questionnaire-projection")
    .map(([path]) => path);

  for (const path of questionnairePaths) {
    const parts = path.split(".");

    if (parts[1] === "basicHistory" && parts.length === 3) {
      const items = clinical.basicHistory as
        | Record<string, { answer: boolean | null; detail: string | null }>
        | undefined;
      const item = items?.[parts[2]];

      if (item) {
        item.answer = null;
        item.detail = null;
      }
    } else if (parts[1] === "dvtRiskFactors" && parts.length === 3) {
      const factors = clinical.dvtRiskFactors as Record<string, boolean | null> | undefined;

      if (factors) {
        factors[parts[2]] = null;
      }
    } else if (parts[1] === "reviewOfSystems" && parts.length === 3) {
      const systems = clinical.reviewOfSystems as
        | Record<string, { findings: Array<{ origin?: string }>; detail: string | null }>
        | undefined;
      const system = systems?.[parts[2]];

      if (system) {
        system.findings = stripOrigin(system.findings);
        system.detail = null;
      }
    } else if (parts[1] === "anesthesiaHistory" && parts.length === 3) {
      const anesthesia = clinical.anesthesiaHistory as Record<string, unknown> | undefined;

      if (anesthesia) {
        anesthesia[parts[2]] = null;
      }
    } else if (
      path === "clinical.noKnownMedications" ||
      path === "clinical.noKnownAllergies" ||
      path === "clinical.noSurgicalHistory"
    ) {
      clinical[parts[1]] = null;
    } else if (path === "clinical.medications") {
      // Filling medications also flipped the none-flag without stamping it.
      if (clinical.noKnownMedications === false) {
        clinical.noKnownMedications = null;
      }
    } else if (path === "clinical.allergies") {
      if (clinical.noKnownAllergies === false) {
        clinical.noKnownAllergies = null;
      }
    } else if (path === "clinical.surgicalHistory") {
      if (clinical.noSurgicalHistory === false) {
        clinical.noSurgicalHistory = null;
      }
    } else if (
      parts[0] === "contacts" &&
      parts.length === 2 &&
      typeof mutable.contacts?.[parts[1]] === "object" &&
      mutable.contacts?.[parts[1]] != null
    ) {
      const contact = mutable.contacts[parts[1]];

      if (contact) {
        contact.name = null;
      }
    } else if (genericOverlayPathAllowed(path)) {
      // Leaves written by applyGenericQuestionnaireChartOverlay: delete
      // questionnaireAnswers entries, reset arrays to empty, scalars to null.
      const segments = path.split(".");
      const parentPath = segments.slice(0, -1).join(".");
      const leaf = segments[segments.length - 1];
      const parent = parentPath
        ? readGenericPath(mutable as Record<string, unknown>, parentPath)
        : mutable;

      if (parent != null && typeof parent === "object") {
        const container = parent as Record<string, unknown>;

        if (parentPath === "clinical.questionnaireAnswers") {
          delete container[leaf];
        } else if (Array.isArray(container[leaf])) {
          container[leaf] = [];
        } else if (container[leaf] !== undefined) {
          container[leaf] = null;
        }
      }
    }
    // clinical.medications / clinical.allergies / clinical.surgicalHistory
    // paths are covered by the origin filters above.

    delete mutable.fieldMeta[path];
  }
}
